
export enum Category {
  REAL_ESTATE = 'REAL_ESTATE',
  CAR = 'CAR'
}

export enum ListingType {
  SALE = 'SALE',
  RENT = 'RENT'
}

export enum UserRole {
  CUSTOMER = 'CUSTOMER',
  PROVIDER = 'PROVIDER',
  BROKER = 'BROKER',
  ADMIN = 'ADMIN'
}

export enum AccountStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

export enum OfferStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  REVISION = 'REVISION'
}

export interface User {
  id: string;
  email?: string;
  phone: string;
  role: UserRole;
  password?: string;

  fullName?: string;

  companyName?: string;
  serviceType?: Category;
  vkn?: string;
  taxOffice?: string; // New: Vergi Dairesi
  contactPerson?: string;
  address?: string;
  bio?: string;
  logoUrl?: string;
  bannerUrl?: string;
  authDocumentUrl?: string;
  taxPlateUrl?: string; // New: Vergi Levhası

  contractApprovedAt?: string; // New: Log Date
  contractIp?: string; // New: Log IP

  credits?: number;
  subscriptionTier?: 'FREE' | 'PRO' | 'ENTERPRISE';
  accountStatus?: AccountStatus;

  rating?: number;
  reviewCount?: number;

  // Broker System Fields
  corporateId?: string; // 8 haneli benzersiz ID (sadece PROVIDER için)
  brokerId?: string; // Bağlı olduğu broker ID
  branchName?: string; // Şube adı (broker'a bağlıysa)
  isBroker?: boolean; // Broker mı?

  createdAt: string;
}

export interface Lead {
  id: string;
  category: Category;
  listingType: ListingType;
  title: string;
  description: string;
  budget: string;
  location: string;
  neighborhoods?: string[];

  details: Record<string, any>;

  maxOffers: number | 'UNLIMITED';
  _count?: { offers: number };

  unlockedBy: string[];
  isUrgent: boolean;
  urgentExpiresAt?: string;
  isSeriousBuyer?: boolean;

  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerId?: string;

  createdAt: string;
}

export interface PortfolioItem {
  id: string;
  providerId: string;
  category: Category;
  title: string;
  description: string;
  price: number;
  currency: string;
  location: string;
  images: string[];
  details: Record<string, any>;
  createdAt: string;
}

export interface Offer {
  id: string;
  leadId: string;
  providerId: string;
  providerName: string;
  price: string;
  description: string;
  imageUrl?: string;

  portfolioItemId?: string;
  portfolioItem?: PortfolioItem;

  status: OfferStatus;
  createdAt: string;
}

export interface Review {
  id: string;
  providerId: string;
  customerId: string;
  customerName: string;
  rating: number;
  comment: string;
  createdAt: string;
  reply?: string;
  repliedAt?: string;
}

export interface AppNotification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
  isRead: boolean;
  createdAt: string;
  link?: string;
}

// Alias for backward compatibility
export type Notification = AppNotification;

export interface Transaction {
  id: string;
  userId: string;
  amount: number;
  creditChange?: number;
  type: 'DEPOSIT' | 'SPEND';
  description: string;
  createdAt: string;
  user?: Partial<User>;
}

export interface SelectOption {
  label: string;
  value: string;
}

export interface Alarm {
  id: string;
  providerId: string;
  category?: Category;
  city: string;
  district?: string;
  criteria?: Record<string, any>;
  createdAt?: string;
}

export interface CreditPackage {
  id: string;
  name: string;
  price: number;
  credits: number; // Represents "Listing Rights Count"
  popular?: boolean;
  description?: string; // Added for Iyzico transparency
}

export interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  content: string;
  attachmentUrl?: string;
  attachmentType?: 'image' | 'file' | 'audio';
  createdAt: string;
  isRead: boolean;
}

export interface Conversation {
  user: User;
  lastMessage: string;
  lastMessageDate: string;
  unreadCount: number;
}

export interface PublicStats {
  happyCustomers: number;
  approvedProviders: number;
  totalLeads: number;
  avgRating: number;
}

// --- NEW DASHBOARD TYPES ---
export interface LiveDashboardData {
  totalPurchasingPower: number; // TL value
  activeDemands24h: number;
  matchRate: number; // Percentage
  avgFillTimeHours: number;

  // Teklif İstatistikleri
  offerStats: {
    totalOffers: number; // Toplam teklif sayısı
    acceptedOffers: number; // Kabul edilen teklif sayısı
    rejectedOffers: number; // Reddedilen teklif sayısı
    revisionOffers: number; // Revize istenen teklif sayısı
    pendingOffers: number; // Bekleyen teklif sayısı
  };

  heatMap: {
    city: string;
    count: number;
    intensity: 'HIGH' | 'MEDIUM' | 'LOW';
  }[];

  opportunityZones: {
    location: string;
    demandCount: number;
    offerCount: number;
    gap: number; // demand - offer
  }[];

  alerts: {
    id: string;
    title: string;
    budget: string;
    hoursWaiting: number;
    location: string;
  }[];
}

// --- EXTERNAL API INTEGRATION TYPES ---

export type AuthType = 'API_KEY' | 'OAUTH2' | 'JWT' | 'BASIC' | 'BEARER';
export type SyncStatus = 'PENDING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED' | 'PARTIAL';

export interface ExternalApiConfig {
  id: string;
  providerId: string;
  providerName: string;
  apiUrl: string;
  authType: AuthType;
  isActive: boolean;
  syncInterval: number; // dakika
  lastSyncAt?: string;
  lastSyncStatus?: SyncStatus;
  lastSyncError?: string;
  mappingConfig: MappingConfig;
  createdAt: string;
  updatedAt: string;
}

export interface MappingConfig {
  titleField?: string;
  descriptionField?: string;
  priceField?: string;
  currencyField?: string;
  locationField?: string;
  imagesField?: string;
  categoryMapping?: Record<string, string>;
  detailsMapping?: Record<string, string>;
  externalIdField?: string;
  externalUrlField?: string;
}

export interface ExternalPortfolioSync {
  id: string;
  configId: string;
  status: SyncStatus;
  itemsTotal: number;
  itemsCreated: number;
  itemsUpdated: number;
  itemsSkipped: number;
  itemsFailed: number;
  errorLog?: string;
  startedAt: string;
  completedAt?: string;
}

export interface SyncStats {
  totalConfigs: number;
  activeConfigs: number;
  successfulSyncs: number;
  failedSyncs: number;
  successRate: number;
}
